/**
 * Sending a reminder from an account: the pieces that are pure.
 *
 * Nothing here sends anything. The product opens the person's own WhatsApp or
 * email with the message filled in, and they send it — so no message leaves
 * under the product's name, and nothing claims a reminder went that did not.
 */

/**
 * The number WhatsApp's click-to-chat link wants: country code then number,
 * digits only.
 *
 * Sheets hold numbers the way people write them — `98765 43210`,
 * `098765-43210`, `+91 98765 43210`, two numbers in one cell. The first number
 * is used. One written without a country code (ten digits, or with a trunk
 * `0`) is given the company's; one that already carries a code keeps it.
 * Anything that cannot be a phone number comes back `null`, and the screen
 * offers no link rather than a link to a stranger.
 */
export function whatsappNumber(raw: string | null | undefined, phoneCountry: string): string | null {
    if (!raw) return null;
    const first = raw.split(/[,;/|]|\bor\b/i)[0]?.trim() ?? '';
    if (!first) return null;

    const international = first.startsWith('+') || first.startsWith('00');
    let digits = first.replace(/\D/g, '');
    if (first.startsWith('00')) digits = digits.slice(2);

    if (!international) {
        const country = phoneCountry.replace(/\D/g, '');
        digits = digits.replace(/^0+/, '');
        const alreadyCoded = country !== '' && digits.startsWith(country) && digits.length > 10;
        if (country && !alreadyCoded) digits = `${country}${digits}`;
    }

    // E.164 allows at most fifteen digits; fewer than eight is not a mobile.
    return digits.length >= 8 && digits.length <= 15 ? digits : null;
}

/** WhatsApp's click-to-chat address, with the message filled in. */
export const whatsappLink = (number: string, text: string): string =>
    `https://wa.me/${number}?text=${encodeURIComponent(text)}`;

/** A `mailto:` address with the message as its body. */
export const mailtoLink = (email: string, subject: string, body: string): string =>
    `mailto:${encodeURIComponent(email.trim()).replace(/%40/g, '@')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
